import { useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { prop } from 'ramda';

import { ANIMATION_DELAY } from 'js/components/common/Link/constants.buzz.js';
import { useIsPageInPopupUrl } from 'js/components/system/Page/hooks';
import { formatPath, getStateWithPrevPath } from 'js/hooks/navigate/useNavigate/helpers';
import { useAppSelector, useDeviceSelector } from 'js/store/hooks';
import { isExternalUrl, openWindow, redirect as redirectUrl } from 'js/utils/url.util';

export const useNavigateBare = ({ replace, redirect }) => {
    const history = useHistory();
    const rootPage = useAppSelector(prop('rootPage'));
    const useTrailingSlash = useAppSelector((state) => state.seo?.useTrailingSlash);
    const { pathLang = '' } = useParams();
    const isInWrapper = useDeviceSelector(prop('isInWrapper'));
    const isPageInPopupUrl = useIsPageInPopupUrl();

    return useCallback((
        path,
        state,
        {
            removeParams = [],
            preserveSearchParams = true,
            preserveHash = false,
            isScrollTop = true
        } = {}
    ) => {
        if (isExternalUrl(path) || redirect) {
            if (isInWrapper) {
                openWindow(path, '_blank');
            } else {
                redirectUrl(path);
            }

            return;
        }

        const { search, hash } = window.location;

        const location = formatPath(
            path,
            pathLang,
            preserveSearchParams ? search : {},
            preserveHash ? hash : '',
            removeParams,
            useTrailingSlash
        );

        if (isScrollTop && !isPageInPopupUrl(location)) {
            setTimeout(() => {
                window.scrollTo({ top: 0 });
            }, ANIMATION_DELAY);
        }

        if (replace) {
            history.replace(location, state);
        } else {
            history.push(location, getStateWithPrevPath(path === rootPage ? '/' : path, state));
        }
    }, [history, isInWrapper, isPageInPopupUrl, pathLang, redirect, replace, rootPage, useTrailingSlash]);
};
